import { put, select, takeLatest } from 'redux-saga/effects';
import { BOOKING_ACTIONS, BOOKING_LOADERS } from './utils';
import {
  setUserBookings,
  toggleLoader,
  cleanerUpdateBooking as cleanerUpdateBookingAction,
  setCurrentBooking,
  deleteBookingRequest as deleteBookingRequestAction,
  removeBooking,
  updateCurrentBookingStatus,
  updateService,
  deleteConfirmedBooking,
  updateCurrentBooking,
  setPendingReviewBookings,
  createNewBooking as createNewBookingAction,
} from './reducer';
import { selectNewBookingDetails } from './selectors';

import { selectIsCleanerMode } from '../app/selectors';
import dayjs from 'dayjs';
import { PayloadAction } from '@reduxjs/toolkit';
import { getBookingDetail } from '@/api/requests/endpoints/GET/getBookingDetail';
import { updateBooking } from '@/api/requests/endpoints/GET/updateBooking';
import { toast } from 'sonner';
import {
  removeBookingFromChat,
  updateBookingStatusByMessageId,
} from '../chat/reducer';
import { deleteBookingRequest } from '@/api/requests/endpoints/POST/deleteBookingRequest';
import { sagaNavigator } from '@/utils/navigator';
import { APP_ROUTES } from '@/utils/appRoutes';
import { updateBookingService } from '@/api/requests/endpoints/POST/updateBookingService';
import { deleteConfirmedBookingById } from '@/api/requests/endpoints/POST/deleteConfirmedBookingById';
import { getBookings } from '@/api/requests/endpoints/GET/getBookings';
import { getUnreviewedBookings } from '@/api/requests/endpoints/GET/getUnreviewedBookings';
import { createNewBooking } from '@/api/requests/endpoints/POST/createNewBooking';

function* createNewBookingSaga({
  payload,
}: ReturnType<typeof createNewBookingAction>) {
  try {
    yield put(
      toggleLoader({ key: BOOKING_LOADERS.CREATE_NEW_BOOKING, value: true }),
    );
    const newBooking = yield select(selectNewBookingDetails);
    const {
      startTime,
      endTime,
      price,
      cleanerId,
      location,
      description,
      requestDate,
    } = newBooking;

    const newBookingPayload = {
      cleanerId,
      price: Number(price),
      startTime,
      endTime,
      description,
      date: dayjs(requestDate * 1000).format('DD/MM/YYYY'),
      paymentMethodId: payload,
      ...location,
    };
    const response = yield createNewBooking(newBookingPayload);
    if (response?.bookingId) {
      yield sagaNavigator(`${APP_ROUTES.BOOKINGS}/${response.bookingId}`);
    }
  } catch (e) {
    console.log(e);
  } finally {
    yield put(
      toggleLoader({ key: BOOKING_LOADERS.CREATE_NEW_BOOKING, value: false }),
    );
  }
}

function* fetchBookingsSaga() {
  try {
    yield put(
      toggleLoader({ key: BOOKING_LOADERS.FETCH_BOOKINGS, value: true }),
    );
    const isCleanerMode = yield select(selectIsCleanerMode);
    const type = isCleanerMode ? 'operator' : 'user';
    const data = yield getBookings(type);
    yield put(setUserBookings(data));
  } catch (e) {
    console.log(e);
    toast.error('Si è verificato un errore! ', {
      style: {
        backgroundColor: '#ff0033',
        color: '#FFF',
      },
      position: 'top-right',
      description:
        'Non è stato possibile recuperare la prenotazioni. Riprova! ',
    });
    yield put(setUserBookings([]));
  } finally {
    yield put(
      toggleLoader({ key: BOOKING_LOADERS.FETCH_BOOKINGS, value: false }),
    );
  }
}

function* getBookingDetailSaga({ payload }: PayloadAction<string>) {
  try {
    yield put(
      toggleLoader({ key: BOOKING_LOADERS.GET_BOOKING_DETAIL, value: true }),
    );
    const data = yield getBookingDetail(payload);
    if (!data) {
      throw new Error('Booking not found');
    }

    yield put(setCurrentBooking(data));
  } catch (e) {
    console.log(e);
  } finally {
    yield put(
      toggleLoader({ key: BOOKING_LOADERS.GET_BOOKING_DETAIL, value: false }),
    );
  }
}

function* cleanerUpdateBookingSaga({
  payload,
}: ReturnType<typeof cleanerUpdateBookingAction>) {
  const { messageId, newStatus, bookingId } = payload;
  try {
    yield put(
      toggleLoader({
        key: BOOKING_LOADERS.CLEANER_UPDATE_BOOKING,
        value: true,
      }),
    );
    yield updateBooking(bookingId, newStatus);
    yield put(
      messageId
        ? updateBookingStatusByMessageId({
            bookingId,
            messageId,
            newStatus,
          })
        : updateCurrentBookingStatus(newStatus),
    );
  } catch (e) {
    console.log(e);
    toast.error('Si è verificato un errore! ', {
      description: 'Non è stato possibile accettare la prenotazione. Riprova! ',
    });
  } finally {
    yield put(
      toggleLoader({
        key: BOOKING_LOADERS.CLEANER_UPDATE_BOOKING,
        value: false,
      }),
    );
  }
}

function* deleteBookingRequestSaga({
  payload,
}: ReturnType<typeof deleteBookingRequestAction>) {
  const { fromChat, bookingId } = payload;
  try {
    yield put(
      toggleLoader({
        key: BOOKING_LOADERS.DELETE_REQUEST,
        value: true,
      }),
    );
    yield deleteBookingRequest(bookingId);

    const action = fromChat ? removeBookingFromChat : removeBooking;
    yield put(action(bookingId));
    yield sagaNavigator(APP_ROUTES.BOOKINGS);
  } catch (e) {
    console.log(e);
    toast.error('Si è verificato un errore! ', {
      description:
        'Non è stato possibile eliminare la richiesta di prenotazione. Riprova! ',
    });
  } finally {
    yield put(
      toggleLoader({
        key: BOOKING_LOADERS.DELETE_REQUEST,
        value: false,
      }),
    );
  }
}

function* updateBookingServiceSaga({
  payload,
}: ReturnType<typeof updateService>) {
  const { type, bookingId } = payload;
  try {
    yield put(
      toggleLoader({
        key: BOOKING_LOADERS.UPDATE_SERVICE,
        value: true,
      }),
    );
    const res = yield updateBookingService(bookingId, type);

    yield put(
      updateCurrentBooking({
        key: 'startedAt',
        value: res.startedAt,
      }),
    );

    yield put(
      updateCurrentBooking({
        key: 'status',
        value: res.status,
      }),
    );
  } catch (e) {
    console.log(e);
    toast.error('Si è verificato un errore! ', {
      description: `Non è stato possibile ${type === 'started' ? 'iniziare' : 'terminare'} il servizio! `,
    });
  } finally {
    yield put(
      toggleLoader({
        key: BOOKING_LOADERS.UPDATE_SERVICE,
        value: false,
      }),
    );
  }
}

function* deleteConfirmedBookingSaga({
  payload,
}: ReturnType<typeof deleteConfirmedBooking>) {
  try {
    yield put(
      toggleLoader({
        key: BOOKING_LOADERS.DELETE_CONFIRMED_BOOKING,
        value: true,
      }),
    );
    const res = yield deleteConfirmedBookingById(payload);
    if (res.error) {
      throw new Error('Something went wrong');
    }
    yield sagaNavigator(APP_ROUTES.BOOKINGS);
  } catch (e) {
    console.log(e);
    toast.error('Si è verificato un errore! ', {
      description: 'Non è stato possibile eliminare la prenotazione. Riprova! ',
    });
  } finally {
    yield put(
      toggleLoader({
        key: BOOKING_LOADERS.DELETE_CONFIRMED_BOOKING,
        value: false,
      }),
    );
  }
}

function* getPendingReviewBookingsSaga() {
  try {
    yield put(
      toggleLoader({
        key: BOOKING_LOADERS.GET_PENDING_REVIEW_BOOKINGS,
        value: true,
      }),
    );
    const res = yield getUnreviewedBookings();
    yield put(setPendingReviewBookings(res));
  } catch (e) {
    console.log(e);
    toast.error('Si è verificato un errore! ', {
      description:
        'Non è stato possibile recuperare le prenotazioni. Riprova! ',
    });
  } finally {
    yield put(
      toggleLoader({
        key: BOOKING_LOADERS.GET_PENDING_REVIEW_BOOKINGS,
        value: false,
      }),
    );
  }
}

export function* bookingsSaga() {
  yield takeLatest(BOOKING_ACTIONS.CREATE_NEW_BOOKING, createNewBookingSaga);
  yield takeLatest(BOOKING_ACTIONS.FETCH_BOOKINGS, fetchBookingsSaga);
  yield takeLatest(BOOKING_ACTIONS.GET_BOOKING_DETAIL, getBookingDetailSaga);
  yield takeLatest(
    BOOKING_ACTIONS.CLEANER_UPDATE_BOOKING,
    cleanerUpdateBookingSaga,
  );
  yield takeLatest(BOOKING_ACTIONS.DELETE_REQUEST, deleteBookingRequestSaga);
  yield takeLatest(BOOKING_ACTIONS.UPDATE_SERVICE, updateBookingServiceSaga);
  yield takeLatest(
    BOOKING_ACTIONS.DELETE_CONFIRMED_BOOKING,
    deleteConfirmedBookingSaga,
  );
  yield takeLatest(
    BOOKING_ACTIONS.GET_PENDING_REVIEW_BOOKINGS,
    getPendingReviewBookingsSaga,
  );
}
